import { CONFIG } from "@/config";
import { ArrowUpRight } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative border-b border-[#EAEAEA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-24 pb-16 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8 fade-up">
            <div className="overline mb-6" data-testid="hero-overline">
              Primary &amp; High School Tutoring
            </div>
            <h1
              data-testid="hero-headline"
              className="font-display font-light tracking-tighter text-[#0A0A0B] text-5xl sm:text-6xl md:text-7xl leading-[0.98]"
            >
              Tutoring that builds
              <br />
              <em className="not-italic font-medium">confident</em> learners,
              <br />
              from primary years up.
            </h1>
            <div
              data-testid="hero-sub"
              className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-[#4A4A52] space-y-5"
            >
              <p>
                I&apos;m <strong className="text-[#0A0A0B] font-semibold">Claire O&rsquo;Connor</strong> —
                a qualified primary school teacher and tutor. I specialise in{" "}
                <strong className="text-[#0A0A0B] font-semibold">one-on-one and small-group tutoring for primary students</strong>,
                with a particular focus on <strong className="text-[#0A0A0B] font-semibold">literacy</strong>—reading,
                comprehension and writing—alongside <strong className="text-[#0A0A0B] font-semibold">numeracy</strong>,
                and I support learners through to senior secondary English.
              </p>
              <p>
                Beyond tutoring, I also provide{" "}
                <strong className="text-[#0A0A0B] font-semibold">academic writing consultancy</strong> for
                senior students, families and schools. My approach combines classroom expertise with
                university-level experience, underpinned by a{" "}
                <strong className="text-[#0A0A0B] font-semibold">Master of Education</strong>,
                a <strong className="text-[#0A0A0B] font-semibold">Journalism degree</strong>,
                a <strong className="text-[#0A0A0B] font-semibold">Certificate in Special Needs Education</strong>,
                years of <strong className="text-[#0A0A0B] font-semibold">primary teaching</strong>,{" "}
                <strong className="text-[#0A0A0B] font-semibold">half a decade of university essay marking</strong>,
                and <strong className="text-[#0A0A0B] font-semibold">coordinating and teaching university literacy courses</strong>.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[#172A4A] text-white text-sm font-semibold tracking-wide px-7 py-4 transition-all hover:-translate-y-0.5"
              >
                Enquire about tutoring
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="#reviews"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 border border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F]/[0.04] text-sm font-semibold tracking-wide px-7 py-4 transition-colors"
              >
                Read client reviews
              </a>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div
              data-testid="hero-portrait"
              className="relative aspect-[4/5] w-full max-w-[280px] md:max-w-[320px] mx-auto lg:mx-0 lg:ml-auto bg-[#EFEAE2] border border-[#EAEAEA] overflow-hidden fade-up"
              style={{ animationDelay: "120ms" }}
            >
              <img
                src="/images/headshot.jpg"
                alt="Portrait of Claire O'Connor, literacy consultant"
                className="absolute inset-0 w-full h-full object-cover object-top"
                loading="eager"
              />
              <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#0A192F]/85 via-[#0A192F]/45 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-white">
                <div className="text-[10px] tracking-[0.24em] uppercase opacity-80">
                  Tutor &amp; Consultant
                </div>
                <div className="font-display text-xl md:text-2xl font-medium mt-1">
                  {CONFIG.personName}
                </div>
                <div className="text-xs opacity-85 mt-1">
                  M.Ed · B.Journ · Literacy Specialist
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 border-t border-[#EAEAEA] pt-10">
          {[
            { k: "Primary Years", v: "Core focus" },
            { k: "Master's", v: "Education" },
            { k: "Journalism", v: "Bachelor's" },
            { k: "Literacy & Numeracy", v: "Specialisation" },
          ].map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="font-display text-2xl md:text-3xl font-medium text-[#0A0A0B]">
                {s.k}
              </div>
              <div className="text-xs tracking-[0.18em] uppercase text-[#4A4A52] mt-2">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
